import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("a", {
  href: "javascript: void(0);",
  role: "button",
  class: "dropdown-toggle",
  id: "search-button",
  "aria-label": "Search button"
}, [
  /*#__PURE__*/_createElementVNode("span", { class: "search-button glyphicon glyphicon-search" })
], -1)
const _hoisted_2 = ["onClick", "onContextmenu", "href"]
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "search-result-highlights" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { key: 0 }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("h4", { class: "text-center" }, "No matching results.", -1)
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = { key: 1 }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("h4", { class: "text-center" }, "There was a problem retrieving search results.", -1)
const _hoisted_11 = [
  _hoisted_10
]

import { ref, watch, onMounted } from "vue";
import { isMobile } from "mobile-device-detect";
import { debug as d } from "debug";
import axios from "axios";
import { Dropdown } from "uiv";
import { useRouter } from "vue-router";
import cms from "../services/cms/cms";
import { CmsResourceLink } from "../services/cms/cms-service";
import { CmsDocumentTypes } from "../services/cms/cms-type-definitions";
import { SearchClickApiModel, SearchResultApiModel, SearchResultsApiModel } from "../api-models";


export default /*#__PURE__*/_defineComponent({
  __name: 'search-bar',
  props: {
    sideNavIsOpen: { type: Boolean }
  },
  setup(__props: any) {

const debug = d("cs.SearchBar");

const isTabletOrMobile = ref(isMobile);
const show = ref(false);
const query = ref("");
const model = ref<SearchResultsApiModel | null>(null);
const serverCallTimeout = ref<any>(null);
const isLoading = ref(false);
const hasError = ref(false);

const props = __props;

const router = useRouter();

// template refs
const searchbox = ref<HTMLInputElement | null>(null);
const dropdown = ref<HTMLElement | null>(null);

function performSearch(): void {
  const sanitizedQuery = (query.value ?? "").trim().replace(/[^\w* ]/g, "");
  if (sanitizedQuery.length < 3) {
    model.value = null;
    return;
  }

  // Simple debounce to avoid hammering the server
  if (serverCallTimeout.value) {
    clearTimeout(serverCallTimeout.value);
  }
  serverCallTimeout.value = setTimeout(() => {
    debug("Searching", sanitizedQuery);
    isLoading.value = true;
    hasError.value = false;
    serverCallTimeout.value = null;
    axios
      .get("/api/search", {
        params: {
          query: sanitizedQuery,
        },
      })
      .then((resp): void => {
        const data = resp.data as SearchResultsApiModel;
        model.value = data;
        debug(`Got results for: ${sanitizedQuery}`);
        isLoading.value = false;
      })
      .catch((err) => {
        debug(`Got an error for: ${sanitizedQuery}`, err);
        isLoading.value = false;
        hasError.value = true;
        model.value = null;
      });
  }, 500);
}

watch(show, (newValue: boolean, oldValue: boolean) => {
  if (!oldValue && newValue) {
    setTimeout(() => {
      (searchbox.value as HTMLInputElement).focus();
    }, 100);
  } else {
    query.value = "";
    model.value = null;
    hasError.value = false;
    isLoading.value = false;
    serverCallTimeout.value = null;
  }
});

watch(query, () => {
  performSearch();
});

function getUrlForResult(result: SearchResultApiModel): string | undefined {
  if (result.type === "Prismic" && !!result.cmsType && result.cmsUid) {
    return (
      result.urlOverride ??
      cms.linkResolver({
        id: result.cmsUid,
        type: result.cmsType,
      } as CmsResourceLink)
    );
  }
  return result.urlOverride ?? undefined;
}

function getResourceTypeName(result: SearchResultApiModel): string {
  switch (result.cmsType as CmsDocumentTypes) {
    case CmsDocumentTypes.CmsResource:
      return "Resource";
    case CmsDocumentTypes.CmsLeaderType:
      return "Leader type";
    case CmsDocumentTypes.CmsLinkResource:
      return result.typeNameOverride || "Resource";
    case CmsDocumentTypes.CmsStrengthItem:
      return "Strength";
    case CmsDocumentTypes.CmsSession:
    case CmsDocumentTypes.CmsSessionWithSlides:
      return "Session";
    case CmsDocumentTypes.CmsWorkshopPage:
      return "Step";
    case CmsDocumentTypes.CmsResourcePlaylist:
      return "Playlist";
    case CmsDocumentTypes.CmsResourceTheme:
      return "Theme";
    case CmsDocumentTypes.CmsSessionWheel:
      return "Wheel";
    case CmsDocumentTypes.CmsTeamPathway:
      return "Team Pathway";
    case CmsDocumentTypes.CmsPersonalPathway:
      return "Personal Pathway";
    default:
      return "Other";
  }
}

function handleClick(
  result: SearchResultApiModel,
  rank: number,
  event: Event,
  navigateAndPreventDefault: boolean,
): void {
  const url = getUrlForResult(result);

  if (!!url && !!model.value) {
    if (navigateAndPreventDefault) {
      event.preventDefault();
    }
    axios
      .post("/api/search", {
        searchCorrelationId: model.value.searchCorrelationId,
        resultRank: rank,
        result,
      } as SearchClickApiModel)
      .finally(() => {
        if (navigateAndPreventDefault) {
          window.location.href = url;
        }
      });
  }
}

function getClosest(element: HTMLElement | null, className: string): HTMLElement | null {
  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    if (currentElement.classList && currentElement.classList.contains(className)) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }
  return null;
}

function fixSafariLayoutBug(): void {
  // Fix an issue where dynamic insertion of the Vue dropdown into the nav
  // within a Razor view leads to incorrect navbars in Safari. This seems to be because
  // navbars is determined on the <component> element included before JS has run.
  // Just removing and re-adding the nav's classes fixes the problem.
  if (!dropdown.value) {
    return;
  }
  const nav = getClosest(dropdown.value, "navbar");
  if (nav) {
    const existingNavbarClasses = Array.from(nav.classList);
    existingNavbarClasses.forEach((c) => nav.classList.remove(c));
    nav.classList.add("hidden");

    setTimeout(() => {
      existingNavbarClasses.forEach((c) => nav.classList.add(c));
      nav.classList.remove("hidden");
    }, 0);
  }
}
onMounted(() => {
  fixSafariLayoutBug();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dropdown), {
    "data-test": "search-bar",
    ref_key: "dropdown",
    ref: dropdown,
    "menu-right": "",
    tag: "li",
    modelValue: show.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((show).value = $event)),
    class: _normalizeClass(["search-bar dropdown-form", { 'has-results': model.value || hasError.value, 'hidden': isTabletOrMobile.value && _ctx.sideNavIsOpen }])
  }, {
    dropdown: _withCtx(() => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(model.value && model.value.results, (result, index) => {
        return (_openBlock(), _createElementBlock("li", {
          class: "search-result",
          key: result.cmsUid
        }, [
          (result.type === 'Prismic')
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                role: "button",
                onClick: ($event: any) => (handleClick(result, index + 1, $event, true)),
                onContextmenu: ($event: any) => (handleClick(result, index + 1, $event, false)),
                href: getUrlForResult(result)
              }, [
                _createElementVNode("img", {
                  class: "search-result-tile",
                  src: result.imageUrl ?? undefined,
                  style: _normalizeStyle({ visibility: result.imageUrl ? 'visible' : 'hidden' }),
                  alt: "Result image"
                }, null, 12, _hoisted_3),
                _createElementVNode("span", {
                  class: _normalizeClass(["search-result-type label label-default", ['search-result-type-' + result.cmsType]])
                }, _toDisplayString(getResourceTypeName(result)), 3),
                _createElementVNode("h4", null, _toDisplayString(result.title), 1),
                _createElementVNode("div", _hoisted_4, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.highlights, (highlight) => {
                    return (_openBlock(), _createElementBlock("p", {
                      key: highlight,
                      innerHTML: highlight
                    }, null, 8, _hoisted_5))
                  }), 128))
                ])
              ], 40, _hoisted_2))
            : _createCommentVNode("", true)
        ]))
      }), 128)),
      (model.value && model.value.results && !model.value.results.length)
        ? (_openBlock(), _createElementBlock("li", _hoisted_6, _hoisted_8))
        : _createCommentVNode("", true),
      (hasError.value)
        ? (_openBlock(), _createElementBlock("li", _hoisted_9, _hoisted_11))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      _hoisted_1,
      (show.value)
        ? _withDirectives((_openBlock(), _createElementBlock("input", {
            key: 0,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
            ref_key: "searchbox",
            ref: searchbox,
            class: "search-box form-control input-lg",
            type: "search",
            placeholder: "Search",
            "aria-label": "Search"
          }, null, 512)), [
            [_vModelText, query.value]
          ])
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue", "class"]))
}
}

})